import { defaultDataIdFromObject } from '@apollo/client';
import { GroupedDomains } from '../GroupedDomains/grouped-domains';
import * as localOnlyFields from './localOnlyFields';

const URI = '/product-information/model';

export const clientBatchKeys = {
  product: '3rd',
  clientOnlyProduct: '3rd',
  productClientOnlyProduct: '3rd',
  serviceAddOns: '2nd',
  reviewSentiments: '2nd',
  reviewPhotos: '2nd',
  reviews: '2nd',
  relatedSearchModel: '2nd'
};

export const serverBatchKeys = {
};

export const getFullyQualifiedAPIPath = ({ useLocalHost, host }) => {

  if (useLocalHost) {
    return 'http://localhost:4000';
  }

  return `${host}${URI}`;
};

export const getRelativeAPIPath = () => {
  return URI;
};

const generateSearchReportId = ({ object }) => {

  const id = [];

  if (object.searchUrl) {
    id.push(object.searchUrl);
  }
  if (object.pageSize) {
    id.push(object.pageSize);
  }
  if (object.startIndex) {
    id.push(object.startIndex);
  }
  if (object.sortBy) {
    id.push(object.sortBy);
  }
  if (object.totalProducts) {
    id.push(object.totalProducts);
  }
  if (object.keyword) {
    id.push(object.keyword);
  }

  if (id.length) {
    return id.join('-');
  }
  return defaultDataIdFromObject(object);
};

const generateSearchId = ({ object }) => {
  if (object.id) return object.id;

  if (object.searchReport) {
    return generateSearchReportId({ object: object.searchReport });
  }
  return defaultDataIdFromObject(object);
};

export const dataIdFromObject = (object) => {
  const dataSource = object.dataSource || object.dataSources;
  // mocking does not provide typename
  // handle usecase of product so that it gets cached
  let typename = object.__typename;
  if (!typename) {
    if (dataSource && object.itemId) {
      if (dataSource === 'Collection') {
        typename = 'CollectionProduct';
      } else {
        typename = 'BaseProduct';
      }
    }
  }
  switch (typename) {

  case 'LoyaltyMembership':
    return object.svocID
      ? object.svocID
      : defaultDataIdFromObject(object);
  case 'Model':
    return object.itemId;
    // case 'InfoAndGuides':
    //   if (object.url) return object.url;
    //   return defaultDataIdFromObject(object);
  case 'SearchModel':
    return generateSearchId({ object });
    // case 'SearchReport':
    //   return generateSearchReportId({ object });
  case 'FbrProduct':
    return object.itemId
      ? `fbr-${object.itemId || object.identifiers?.itemId}`
      : defaultDataIdFromObject(object);
  case 'BaseProduct':
    // toReference will create an id, so always return it if its there
    // @todo this should be done on other types but leaving for now.
    if (object.id) {
      return object.id;
    }
    if (dataSource === 'dynamicRecs' && (object.itemId || object.identifiers?.itemId)) {
      return `dynamicRecs-${object.itemId || object.identifiers?.itemId}`;
    }
    return (object.itemId || object.identifiers?.itemId)
      ? `base-${dataSource || 'catalog'}-${object.itemId || object.identifiers?.itemId}`
      : defaultDataIdFromObject(object);
  case 'DynamicRecsProduct':
    return object.itemId
      ? `dynamicRecs-${object.itemId || object.identifiers?.itemId}`
      : defaultDataIdFromObject(object);
  case 'CollectionProduct':
    return object.itemId
      ? `collection-${object.itemId || object.identifiers?.itemId}`
      : defaultDataIdFromObject(object);
  case 'FBRItem':
    return object?.product?.itemId
      ? `fbr-wrapper${object?.product?.itemId}`
      : defaultDataIdFromObject(object);
    // case 'Image':
    //   return `${object.url}-${object.type || 'no-type'}`;
  default:
    return defaultDataIdFromObject(object); // fall back to default handling
  }
};

const productFieldsToMerge = {
  ...localOnlyFields.Product,
  availabilityType: {
    merge: true
  },
  identifiers: {
    merge: true
  },
  details: {
    merge: true
  },
  fulfillment: {
    merge: true,
  },
  pricing: {
    merge: true
  },
  favoriteDetail: {
    merge: true
  },
  reviews: {
    merge: true
  },
  info: {
    merge: true
  },
  media: {
    merge: true
  },
  taxonomy: {
    merge: true
  },
  installServices: {
    merge: true
  },
  subscription: {
    merge: true
  },
  keyProductFeatures: {
    merge: true
  }
};

export const typePolicies = {
  Query: {
    fields: {
      topDeals: {
        merge: true
      },
      product: {
        keyArgs: ['itemId'],
        read(existingData, {
          args,
          toReference,
          readField
        }) {
          // this cant be correct and never executes
          if (existingData) {
            const dataSource = existingData.dataSource || existingData.dataSources;
            const existingDataSource = readField('dataSource', existingData) || readField('dataSources', existingData);
            if (args.dataSource === 'FBR' && existingDataSource === 'FBR') {
              return existingData;
            } if (args.dataSource === 'dynamicRecs' && existingDataSource === 'dynamicRecs') {
              return existingData;
            } if ((args.dataSource === 'catalog' || !args.dataSource) && existingDataSource === 'catalog') {
              return existingData;
            } if (args.dataSource === 'Collection') {
              return existingData;
              // eslint-disable-next-line max-len
            } if (args.dataSource === 'searchNav' && (existingDataSource === 'searchNav' || dataSource === 'searchNav')) {
              // hovering on supersku swatch of product pod, kicks off
              return existingData;
            }
            // @todo other recommenders

          }

          switch (args.dataSource) {
          case 'dynamicRecs':
            return toReference(dataIdFromObject({
              __typename: 'DynamicRecsProduct',
              id: args.id,
              itemId: args.itemId
            }));
          case 'FBR':
            return toReference(dataIdFromObject({
              __typename: 'FbrProduct',
              id: args.id,
              itemId: args.itemId
            }));
          case 'Collection':
            return toReference(dataIdFromObject({
              __typename: 'CollectionProduct',
              id: args.id,
              itemId: args.itemId
            }));

          case 'relatedSearch':
            return toReference(dataIdFromObject({
              __typename: 'BaseProduct',
              id: args.id,
              itemId: args.itemId,
              dataSources: 'dynamicRecs'
            }));

          case 'searchNav':
            return toReference({
              __typename: 'BaseProduct',
              id: dataIdFromObject({
                __typename: 'BaseProduct',
                id: args.id,
                itemId: args.itemId,
                dataSources: 'searchNav'
              })
            });

          case 'packages':
            return toReference({
              __typename: 'BaseProduct',
              id: dataIdFromObject({
                __typename: 'BaseProduct',
                id: args.id,
                itemId: args.itemId,
                dataSources: 'packages'
              })
            });

          default:

            return toReference({
              __typename: 'BaseProduct',
              id: dataIdFromObject({
                __typename: 'BaseProduct',
                id: args.id,
                itemId: args.itemId,
                dataSources: args.dataSource
              })
            });
          }
        },
        merge(existing, incoming, opts) {
          if (!existing) {
            // if (opts.args.dataSource) {
            //   const ref = opts.toReference({
            //     __typename: 'BaseProduct',
            //     id: dataIdFromObject({
            //       __typename: 'BaseProduct',
            //       id: opts.args.id,
            //       itemId: opts.args.itemId,
            //       dataSources: opts.args.dataSource
            //     })
            //   });
            //   return ref;
            // }
            if (opts.args.dataSource) {
              return { ...incoming, dataSources: opts.args.dataSource };
            }

            return incoming;
          }
          const incomingItemId = opts.readField('itemId', incoming);
          const exstingItemId = opts.readField('itemId', existing);
          if (incomingItemId !== exstingItemId) return incoming;

          const incomingDS = opts.readField('dataSource', incoming) || opts.readField('dataSources', incoming);
          const existingDS = opts.readField('dataSource', existing) || opts.readField('dataSources', existing);

          if (incomingDS !== existingDS) {
            return incoming;
          }

          return opts.mergeObjects(existing, incoming);
        }
      },
      dynamicRecs: {
        merge: true
      },
      // commenting out. this makes no sense and breaks collection list
      // collection(existingData, { args, toReference }) {
      //   if (existingData) {
      //     return existingData;
      //   }
      //
      //   return toReference({
      //     __typename: 'BaseProduct',
      //     id: dataIdFromObject({
      //       __typename: 'BaseProduct',
      //       id: args.id,
      //       itemId: args.itemId,
      //       dataSources: 'searchNav'
      //     })
      //   });
      // },
      // reviews: {
      //   merge: true,
      //   keyArgs: ['itemId']
      // }
    }
  },
  DynamicRecsProduct: {
    fields: productFieldsToMerge
  },
  BaseProduct: {
    fields: productFieldsToMerge
  },
  FbrProduct: {
    fields: productFieldsToMerge
  },
  CollectionProduct: {
    fields: productFieldsToMerge
  },
  Details: {
    // keyFields: false,
    fields: {
      collection: {
        merge: true
      }
    }
  },
  Pricing: {
    fields: {
      alternate: {
        merge: true
      },
      promotion: {
        merge: true
      }
    }
  },
  Bundle: {
    fields: {
      products: {
        merge: (existing, incoming, opts) => {
          // if irg has a lot of items it creates a perf bottleneck
          // irg needs to be refactored with pagination.
          return (incoming || []).slice(0, 18);
        }
      }
    }
  },
  Alternate: {
    fields: {
      unit: {
        merge: true
      }
    }
  },
  RatingsReviews: {
    fields: {
      alternate: {
        merge: true
      }
    }
  },
  Media: {
    fields: {
      ...localOnlyFields.Media
    }
  },
  AvailabilityType: {
    fields: {
      ...localOnlyFields.AvailabilityType
    }
  },
  Fulfillment: {
    fields: {
      ...localOnlyFields.Fulfillment
    }
  },
  Info: {
    fields: {
      ...localOnlyFields.Info
    }
  },
  Identifiers: {
    fields: {
      ...localOnlyFields.Identifiers
    }
  },
};

export const getBatchedDomains = (domains = [], group, ssr) => {
  if (group.length === 0) return domains;

  let primaryGroup;
  if (group.server && ssr) {
    primaryGroup = group.server;
  } else if (group.client) {
    primaryGroup = group.client;
  } else if (Array.isArray(group)) {
    primaryGroup = group;
  }
  if (!primaryGroup || primaryGroup.length === 0) return domains;

  // if domains contains any of the above, add all the above to the set
  const set = new Set();
  domains.forEach((domain) => set.add(domain));
  const count = primaryGroup.reduce((acc, cur) => (acc + set.has(cur) ? 1 : 0), 0);
  if (count > 0) {
    primaryGroup.forEach(((domain) => set.add(domain)));
  }
  return Array.from(set)
    .sort();

};

export const spreadGroupedDomains = ({ domains }) => {
  const all = new Set();
  (domains || []).forEach((cur) => {
    if (!/^\$/.test(cur)) {
      all.add(cur);
    } else if (GroupedDomains[cur]) {
      GroupedDomains[cur].forEach((domain) => all.add(domain));
    }
  });

  return Array.from(all);
};

export const hasAllDomains = ({ product, domains: inputDomains }) => {
  const domains = spreadGroupedDomains({ domains: inputDomains });
  const keys = Object.keys(product);
  const result = domains.every((domain) => keys.indexOf(domain) > -1);
  return result;
};
